import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { MessageSquare, Send, X, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function FeedbackWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit() {
    if (!message.trim()) {
      toast.error('Please enter your feedback');
      return;
    }

    try {
      setSending(true);
      const response = await fetch(`${API_URL}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: message.trim(),
          context: {
            url: window.location.pathname,
            timestamp: new Date().toISOString()
          }
        })
      });

      if (response.ok) {
        setSent(true);
        setMessage('');
        toast.success('Thank you for your feedback!');
        setTimeout(() => {
          setIsOpen(false);
          setSent(false);
        }, 2000);
      } else {
        toast.error('Failed to submit feedback');
      }
    } catch (error) {
      toast.error('Failed to submit feedback');
    } finally {
      setSending(false);
    }
  }

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-4 bg-indigo-500 hover:bg-indigo-600 text-white rounded-full shadow-lg z-50"
        data-testid="feedback-trigger"
      >
        <MessageSquare className="w-5 h-5 mr-2" />
        Feedback
      </Button>
    );
  }

  return (
    <div
      className="fixed bottom-20 right-4 w-80 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50"
      data-testid="feedback-widget"
    >
      <div className="flex items-center justify-between p-3 border-b border-slate-700">
        <span className="text-white font-medium flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-indigo-400" />
          Send Feedback
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsOpen(false)}
          className="text-slate-400 hover:text-white p-1"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
      <div className="p-4 space-y-4">
        {sent ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <Check className="w-6 h-6 text-green-400" />
            </div>
            <p className="text-white font-medium">Thank you!</p>
            <p className="text-slate-400 text-sm">Your feedback helps us improve</p>
          </div>
        ) : (
          <>
            <Textarea
              placeholder="How can we make ClientKit better?"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="bg-slate-900 border-slate-700 text-white min-h-[100px]"
            />
            <Button
              onClick={handleSubmit}
              disabled={sending}
              className="w-full bg-indigo-500 hover:bg-indigo-600 text-white"
            >
              <Send className="w-4 h-4 mr-2" />
              {sending ? 'Sending…' : 'Send Feedback'}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default FeedbackWidget;
